<template>
  <div class="hypo-total-period-table">
    <mds-data-table size="medium" :header-configs="headers" :row-data="componentData.rows">
      <template v-slot:header>
        <thead>
          <tr class="table-header-names">
            <th
              v-for="(column, index) in componentData.columns"
              :key="column.value"
              class="table-header-name-column"
              :style="{ width: getColWidth(column.value) }"
            >
              <div :class="{ 'table-header-name-column__wrapper': index === 0 }">
                <div
                  :id="column.value"
                  class="table-header-name"
                  @mouseover="e => showName(column.value)"
                  @mouseleave="e => hideName()"
                >
                  {{ formatHeaderText(column.value, index) }}
                </div>
                <mds-tooltip
                  v-if="currentTrigger === column.value"
                  v-model="toggle"
                  class="return-tooltip"
                  :triggered-by="currentTrigger"
                  :position="['bottom-center']"
                >
                  {{ formatHeaderText(column.value, index) }}
                </mds-tooltip>
                <slot v-if="index === 0"></slot>
              </div>
            </th>
          </tr>
        </thead>
      </template>
      <template v-slot:body>
        <mds-data-table-body :header-configs="headers" :row-data="componentData.rows">
          <template v-slot:body-cell="{ headerConfig, rowData }">
            <div :class="headerConfig.italic ? 'italic' : ''">
              {{
                headerConfig.transform
                  ? headerConfig.transform(rowData[headerConfig.fieldName])
                  : rowData[headerConfig.fieldName]
              }}
            </div>
          </template>
        </mds-data-table-body>
      </template>
    </mds-data-table>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsTooltip from '@mds/tooltip';
import { MdsDataTable, MdsDataTableBody } from '@mds/data-table';
import defaultConfig from './_config';

const ignoredTooltipKeys = [
  'portfolio0',
  'portfolio2',
  'name',
  'Benchmark',
  'benchmark1',
  'benchmark3',
];

export default {
  name: 'hypo-total-period-table',
  components: {
    MdsDataTable,
    MdsDataTableBody,
    MdsTooltip,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => defaultConfig,
    },
    componentData: {
      type: [String, Object],
    },
  },
  data() {
    return {
      defaultConfig: {
        intlNamespace: this.mwcId,
        ...defaultConfig,
      },
      toggle: false,
      currentTrigger: '',
      colWidthsMap: {
        1: () => '50%',
        2: () => '33%',
        3: () => '25%',
        4: value => (value === 'name' ? '30%' : '17.5%'),
        default: () => '50%',
      },
    };
  },
  computed: {
    computedColWidth() {
      const totalCols = this.componentData.columns.length;
      return 100 / totalCols;
    },
    componentConfig() {
      return this.getComponentConfig().settings || defaultConfig;
    },
    headers() {
      const headers = this.componentData.columns.map(item => {
        const column = {
          fieldName: item.value,
          sortable: false,
          align: item.value === 'name' ? 'left' : 'right',
          class: 'table-cell',
          style: {
            width: this.getColWidth(item.value),
          },
          italic: item.italic,
        };
        if (item.value !== 'name') {
          column.transform = value => {
            if (!value) {
              return '—';
            }
            const newValue =
              typeof value === 'number'
                ? parseFloat(value).toFixed(this.componentConfig.decimalDigits)
                : value;
            return newValue;
          };
        }
        return column;
      });
      return headers;
    },
    labels() {
      return {
        portfolio: this.translate('portfolio'),
        return: this.translate('return'),
        benchmark: this.translate('benchmark'),
        holding: this.translate('holding'),
      };
    },
  },
  methods: {
    showName(key) {
      this.currentTrigger = key;
      this.toggle = ignoredTooltipKeys.indexOf(key) < 0;
    },
    hideName() {
      this.toggle = false;
    },
    getColWidth(value) {
      const colNumber = this.componentData.columns.length;
      const defaultWidth = value === 'name' ? '30%' : '17%.5';
      return colNumber > 5 ? this.computedColWidth : defaultWidth;
    },
    formatHeaderText(value, index) {
      return value === 'name' ? `${this.labels.return} %` : this.formatHeaderLabel(value, index);
    },
    formatHeaderLabel(value, index) {
      if (this.componentData.isInvestmentFlow) {
        return value;
      }
      const type = value.slice(0, -1);
      // if the third item is a portfolio it needs to add the number at the end
      const thirdItemType =
        this.componentData.columns.length > 2
          ? this.componentData.columns[2].value.slice(0, -1)
          : '';
      let label = this.labels[type];
      if (this.componentData.columns.length > 3 || thirdItemType === 'portfolio') {
        if (type === 'portfolio') {
          label = index - 1 === 0 ? `${label} 1` : `${label} 2`;
        } else {
          label = index - 1 === 1 ? `${label} 1` : `${label} 2`;
        }
      }
      return label;
    },
  },
};
</script>

<style lang="scss">
@import '~@mds/constants';
@import './styles/variables';

.#{$component-ns} {
  background-color: $mds-background-color-white;
  text-align: center;
  table {
    table-layout: fixed;
    th .table-header-name {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
  .element {
    background-color: $mds-background-color-white;
    text-align: center;
    display: flex;
  }
  .table-header-name-column {
    padding: 0px;
    text-align: right;
    line-height: 31px;
    &:first-child {
      color: #1e1e1e;
      // width: 33%;
      text-align: left;
    }
    @media only screen and (max-width: 600px) {
      font-size: 12px;
    }
    &__wrapper {
      display: flex;
      align-items: center;
    }
  }
  .table-header-names {
    font-size: 14px;
    line-height: 18px;
    color: #5e5e5e;
  }
  .table-cell {
    padding: 4px 0px;
    font-size: 16px;
    @media only screen and (max-width: 600px) {
      font-size: 14px;
    }
  }
  .italic {
    font-style: italic;
  }
}
</style>
